import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import BillsPage from "./pages/BillsPage";
import TaxDashboardPage from "./pages/TaxDashboardPage";
import VendorLedgerPage from "./pages/VendorLedgerPage";
import AIChatPage from "./pages/AIChatPage";
import ExportsPage from "./pages/ExportsPage";
import VendorsPage from "./pages/VendorsPage";
import VendorDetailPage from "./pages/VendorDetailPage";
import Gstr2bPage from "./pages/Gstr2bPage";
import CreditDebitNotesPage from "./pages/CreditDebitNotesPage";
import AuditLogPage from "./pages/AuditLogPage";
import PnLPage from "./pages/PnLPage";
import SalesCapturePage from "./pages/SalesCapturePage";
import PartnersPage from "./pages/PartnersPage";
import TransactionDashboardPage from "./pages/TransactionDashboardPage";
import DeletionRequestsPage from "./pages/DeletionRequestsPage";
import CashDashboardPage from "./pages/CashDashboardPage";
import CashFlowPage from "./pages/CashFlowPage";
import PayrollPayoutsPage from "./pages/PayrollPayoutsPage";
import ServiceChargePage from "./pages/ServiceChargePage";
import TargetsPage from "./pages/TargetsPage";
import ObligationsPage from "./pages/ObligationsPage";
import PaymentCommandsPage from "./pages/PaymentCommandsPage";

// Protected Route wrapper
const ProtectedRoute = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Layout>
      <Outlet />
    </Layout>
  );
};

// Public Route wrapper (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background">
            {/* Noise overlay */}
            <div className="noise-overlay" />
            
            <Routes>
              {/* Public routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <LoginPage />
                  </PublicRoute>
                }
              />

              {/* Protected routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/pnl" element={<PnLPage />} />
                <Route path="/targets" element={<TargetsPage />} />
                <Route path="/obligations" element={<ObligationsPage />} />
                <Route path="/payment-commands" element={<PaymentCommandsPage />} />
                <Route path="/sales-capture" element={<SalesCapturePage />} />
                <Route path="/deletion-requests" element={<DeletionRequestsPage />} />
                <Route path="/bills" element={<BillsPage />} />
                <Route path="/credit-debit-notes" element={<CreditDebitNotesPage />} />
                <Route path="/vendors" element={<VendorsPage />} />
                <Route path="/vendors/:vendorId" element={<VendorDetailPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/transactions" element={<TransactionDashboardPage />} />
                <Route path="/cash" element={<CashDashboardPage />} />
                <Route path="/cash-flow" element={<CashFlowPage />} />
                <Route path="/payroll" element={<PayrollPayoutsPage />} />
                <Route path="/service-charge" element={<ServiceChargePage />} />
                <Route path="/ledger" element={<VendorLedgerPage />} />
                <Route path="/tax" element={<TaxDashboardPage />} />
                <Route path="/gstr2b" element={<Gstr2bPage />} />
                <Route path="/audit" element={<AuditLogPage />} />
                <Route path="/chat" element={<AIChatPage />} />
                <Route path="/exports" element={<ExportsPage />} />
              </Route>

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>

            <Toaster />
          </div>
        </BrowserRouter>
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
